import LiveStreamPlayer from "@/components/LiveStreamPlayer";
import {usePrograms} from "@/hooks/usePrograms";
import {useEffect, useMemo, useState} from "react";
import ProgramSlideCard from "@/features/home/components/ProgramSlideCard.tsx";

const DEFAULT_BANNER =
    "https://images.pexels.com/photos/80454/tree-desert-namibia-dead-vlei-80454.jpeg";

const TZ = "Africa/Windhoek";

type ProgramApi = {
    id: string;
    title: string;
    description: string;
    producer: string;
    imageUrl?: string | null;
    schedules?: Array<{
        id?: string;
        dayOfWeek: string;
        startTime: string;
        endTime: string;
        producerOverride?: string | null
    }>;
    overrides?: Array<{
        id?: string;
        date: string;
        startTime: string;
        endTime: string;
        producerOverride?: string | null;
        titleOverride?: string | null;
        descriptionOverride?: string | null;
        imageUrlOverride?: string | null
    }>;
};

type Entry = {
    key: string;
    programId: string;
    title: string;
    description: string;
    producer: string;
    imageUrl?: string | null;
    startTime: string; // HH:mm
    endTime: string;   // HH:mm
    source: "schedule" | "override";
};

const DAY_MAP: Record<string, "MONDAY" | "TUESDAY" | "WEDNESDAY" | "THURSDAY" | "FRIDAY" | "SATURDAY" | "SUNDAY"> = {
    monday: "MONDAY", tuesday: "TUESDAY", wednesday: "WEDNESDAY", thursday: "THURSDAY",
    friday: "FRIDAY", saturday: "SATURDAY", sunday: "SUNDAY",
};

function todayTokens(tz = TZ) {
    const now = new Date();
    const weekday = new Intl.DateTimeFormat("en-US", {weekday: "long", timeZone: tz})
        .format(now).toLowerCase();
    const isoDate = new Intl.DateTimeFormat("en-CA", {timeZone: tz}).format(now); // YYYY-MM-DD
    return {dayOfWeek: DAY_MAP[weekday], isoDate};
}

function hhmmToMin(hhmm: string): number {
    const [h, m] = hhmm.split(":").map((n) => parseInt(n, 10));
    return h * 60 + m;
}

function isNowInWindow(nowMin: number, start: string, end: string): boolean {
    const s = hhmmToMin(start), e = hhmmToMin(end);
    if (e > s) return nowMin >= s && nowMin < e;
    return nowMin >= s || nowMin < e; // crosses midnight
}

function getNowMinutesTz(tz = TZ): number {
    const now = new Date();
    const [hh, mm] = new Intl.DateTimeFormat("en-GB", {
        timeZone: tz,
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23"
    })
        .format(now).split(":");
    return parseInt(hh, 10) * 60 + parseInt(mm, 10);
}

function toEntriesForToday(p: ProgramApi, dayOfWeek: string, isoDate: string): Entry[] {
    const entries: Entry[] = [];

    (p.schedules ?? []).filter(s => s.dayOfWeek === dayOfWeek).forEach((s, i) => {
        entries.push({
            key: `S-${p.id}-${s.id ?? i}`,
            programId: p.id,
            title: p.title,
            description: p.description,
            producer: s.producerOverride ?? p.producer,
            imageUrl: p.imageUrl ?? null,
            startTime: s.startTime,
            endTime: s.endTime,
            source: "schedule",
        });
    });

    (p.overrides ?? []).filter(o => o.date === isoDate).forEach((o, i) => {
        entries.push({
            key: `O-${p.id}-${o.id ?? i}`,
            programId: p.id,
            title: o.titleOverride ?? p.title,
            description: o.descriptionOverride ?? p.description,
            producer: o.producerOverride ?? p.producer,
            imageUrl: o.imageUrlOverride ?? p.imageUrl ?? null,
            startTime: o.startTime,
            endTime: o.endTime,
            source: "override",
        });
    });

    // prefer override if same window
    const byWindow = new Map<string, Entry>();
    for (const e of entries) {
        const k = `${e.startTime}-${e.endTime}`;
        const prev = byWindow.get(k);
        if (!prev || (e.source === "override" && prev.source === "schedule")) byWindow.set(k, e);
    }
    return Array.from(byWindow.values());
}

function orderByStart(entries: Entry[]) {
    return entries.slice().sort((a, b) => a.startTime.localeCompare(b.startTime));
}

export function HomeBanner() {

    const {data} = usePrograms({page: 0, size: 200, sortBy: "title", sortOrder: "asc"});
    const programs = (data?.items as ProgramApi[] | undefined) ?? [];

    const {dayOfWeek, isoDate} = useMemo(() => todayTokens(TZ), []);
    const todaysEntries = useMemo(() => {
        const all = programs.flatMap((p) => toEntriesForToday(p, dayOfWeek, isoDate));
        return orderByStart(all);
    }, [programs, dayOfWeek, isoDate]);

    const [nowMin, setNowMin] = useState(() => getNowMinutesTz(TZ));
    useEffect(() => {
        const id = setInterval(() => setNowMin(getNowMinutesTz(TZ)), 30_000);
        return () => clearInterval(id);
    }, []);

    const current = useMemo(
        () => todaysEntries.find((e) => isNowInWindow(nowMin, e.startTime, e.endTime)),
        [todaysEntries, nowMin]
    );

    const slides = useMemo(() => {
        const ordered = (() => {
            if (!current) return todaysEntries;
            const idx = todaysEntries.findIndex((e) => e.key === current.key);
            return [...todaysEntries.slice(idx), ...todaysEntries.slice(0, idx)];
        })();

        return ordered.map((e) => ({
            key: e.key,
            title: e.title,
            description: e.description,
            producer: e.producer,
            imageUrl: e.imageUrl || DEFAULT_BANNER,
            isLive: current ? e.key === current.key : false, // mark current
        }));
    }, [todaysEntries, current]);

    const [index, setIndex] = useState(0);
    useEffect(() => setIndex(0), [slides[0]?.key]);

    useEffect(() => {
        if (slides.length <= 1) return;
        const id = setInterval(() => setIndex((prev) => (prev + 1) % slides.length), 7000);
        return () => clearInterval(id);
    }, [slides.length]);

    const playerTitle = current ? current.title : "Kosmos 94.1";
    const playerProducer = current ? current.producer : "";
    const playerDescription = current ? current.description : "";

    return (
        <div className="bg-gradient-to-b from-[#1E2939] to-[#112E5C]">
            <div className="relative">
                <div className="mx-auto max-w-7xl">
                    <div className="relative z-10 lg:w-full lg:max-w-2xl">
                        {/* 2) Make the left-edge polygon use the same gradient */}
                        <svg
                            viewBox="0 0 100 100"
                            preserveAspectRatio="none"
                            aria-hidden="true"
                            className="absolute inset-y-0 right-8 hidden h-full w-80 translate-x-1/2 transform lg:block"
                        >
                            <defs>
                                <linearGradient id="heroGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#1E2939"/>
                                    <stop offset="100%" stopColor="#112E5C"/>
                                </linearGradient>
                            </defs>
                            <polygon points="0,0 90,0 50,100 0,100" fill="url(#heroGradient)"/>
                        </svg>

                        <div className="relative px-6 py-32 sm:py-40 lg:px-8 lg:py-26 lg:pr-0">
                            <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-xl">
                                <h1 className="text-5xl font-semibold tracking-tight text-pretty text-white sm:text-7xl">
                                    Kosmos 94.1
                                </h1>
                                <p className="mt-8 text-lg font-medium text-pretty text-lime-300 sm:text-xl/8">
                                    Kosmos 94.1 is Namibië se gunsteling Afrikaanse radiostasie.
                                </p>

                                <LiveStreamPlayer
                                    className="mt-6"
                                    programTitle={playerTitle}
                                    programProducer={playerProducer}
                                    programDescription={playerDescription}
                                />

                                <div className="mt-6 flex space-x-4">
                                    <a href="https://apps.apple.com/us/app/kosmos-94-1/id6752960000" target="_blank"
                                       rel="noopener noreferrer">
                                        <img src="/app_store.svg" alt="Download on the App Store" className="h-12"/>
                                    </a>
                                    <a href="https://play.google.com/store/apps/details?id=com.kosmos_namibia.app"
                                       target="_blank" rel="noopener noreferrer">
                                        <img src="/play_store.png" alt="Get it on Google Play" className="h-12"/>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-gray-50 lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2">
                    <div
                        className="relative overflow-hidden aspect-[4/3] min-h-[260px] sm:aspect-[16/9] lg:h-full lg:aspect-auto">
                        {slides.map((p, i) => (
                            <div
                                key={p.key}
                                className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${i === index ? "opacity-100" : "opacity-0"}`}
                                aria-hidden={i !== index}
                            >
                                <ProgramSlideCard
                                    imageUrl={p.imageUrl}
                                    title={p.title}
                                    description={p.description}
                                    producer={p.producer}
                                    isLive={p.isLive}
                                />
                            </div>
                        ))}

                        {slides.length === 0 && (
                            <ProgramSlideCard
                                imageUrl={DEFAULT_BANNER}
                                title="Kosmos 94.1"
                                description="Namibië se gunsteling Afrikaanse radiostasie."
                                producer=""
                                isLive={false}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}